import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { 
  Lock, 
  Email, 
  Visibility, 
  VisibilityOff,
  Google,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const LoginPage = () => {
  const paymentHoldEnabled = __PAYMENT_HOLD_ENABLED__;
  const paymentHoldMessage = __PAYMENT_HOLD_MESSAGE__;

  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginGoogle, loading, error } = useAuth();
  
  // Estado del formulario
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  
  // Estado para Google OAuth
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // Redirección guardada
  const from = location.state?.from?.pathname || '/billing';

  // Validar email
  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Manejar submit del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (paymentHoldEnabled) {
      return;
    }
    
    // Validaciones
    if (!email || !password) {
      setLocalError('Por favor complete todos los campos');
      return;
    }
    
    if (!isValidEmail(email)) {
      setLocalError('Ingrese un email válido');
      return;
    }

    // Usar Firebase Auth a través del AuthContext
    const result = await login(email, password);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setLocalError(result.error || 'Error al iniciar sesión');
    }
  };

  // Manejar login con Google - usa Firebase Popup
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setLocalError('');
    
    try {
      if (paymentHoldEnabled) {
        return;
      }

      // Usar Firebase Auth a través del AuthContext
      const result = await loginGoogle();
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setLocalError(result.error || 'Error con Google OAuth');
      }
    } catch (err) {
      setLocalError(err.message || 'Error de conexión con Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Manejar toggle de mostrar contraseña
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1565c0 100%)',
        backgroundSize: '100% 100%',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <Card 
        sx={{ 
          maxWidth: 420, 
          width: '100%', 
          mx: 2,
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo y Título */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 8px 20px rgba(26, 35, 126, 0.3)',
              }}
            >
              <Lock sx={{ color: 'white', fontSize: 36 }} />
            </Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                color: '#1a237e',
                mb: 1,
              }}
            >
              Bienvenido
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sistema de Gestión Comercial
            </Typography>
          </Box>

          {paymentHoldEnabled && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {paymentHoldMessage}
            </Alert>
          )}

          {/* Errores */}
          {(error || localError) && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setLocalError('')}
            >
              {error || localError}
            </Alert>
          )}

          {/* Formulario de Login */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Correo electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              autoComplete="email"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3949ab',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1a237e',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3949ab',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1a237e',
                  },
                },
              }}
            />
            
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={paymentHoldEnabled || loading}
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
                boxShadow: '0 4px 14px rgba(26, 35, 126, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0d47a1 0%, #1a237e 100%)',
                  boxShadow: '0 6px 20px rgba(26, 35, 126, 0.5)',
                },
                '&:disabled': {
                  background: '#e0e0e0',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          {/* Divider */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              o
            </Typography>
          </Divider>

          {/* Botón Google OAuth */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleGoogleLogin}
            disabled={paymentHoldEnabled || googleLoading}
            startIcon={googleLoading ? <CircularProgress size={20} /> : <Google />}
            sx={{ 
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              borderColor: '#e0e0e0',
              color: '#5f6368',
              '&:hover': {
                borderColor: '#d2d2d2',
                backgroundColor: '#f8f9fa',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
              },
            }}
          >
            {googleLoading ? 'Conectando...' : 'Continuar con Google'}
          </Button>

          {/* Información de Firebase Auth */}
          <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
              Autenticación segura
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Usa tu correo y contraseña o inicia sesión con Google para acceder al sistema.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
